import React, { useState } from 'react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card } from '../../components/cards/Card';
import { Button } from '../../components/buttons/Button';
import { StatCard } from '../../components/cards/StatCard';
import { Badge } from '../../components/common/Badge';
import { PropertyForm } from '../../components/forms/PropertyForm';
import {
   LayoutDashboard, Users, Building, AlertCircle,
   ShieldCheck, Search, Trash2, Edit2, Eye, Settings, Activity, DoorOpen, UserRound, Info,
   Gift, Check, X, GraduationCap, Briefcase, Home, ChevronLeft, Banknote, Mail, Image,
   History, MessageSquare, ClipboardList, UserPlus
} from 'lucide-react';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole, User, Property, Booking } from '../../types';
import { useNavigate } from 'react-router-dom';
import { InternalNotes } from '../../components/common/InternalNotes';

export const AdminDashboard: React.FC = () => {
   const [activeTab, setActiveTab] = useState('overview');
   const {
      properties, deleteProperty, updateProperty,
      users, updateUser, deleteUser,
      referralCampaigns, addReferralCampaign, toggleReferralCampaign,
      bookings, bookingsError, withdrawalRequests, updateWithdrawalStatus,
      landlords, addLandlord, updateLandlord, deleteLandlord,
      auditLogs, internalNotes, addNote, logAction,
      messages, updateBooking, reassignPropertyAgent
   } = useData();
   const { isLandlordEnabled, toggleLandlordFeature } = useAuth();
   const navigate = useNavigate();

   // --- USER MGMT STATE ---
   const [userSearch, setUserSearch] = useState('');
   const [roleFilter, setRoleFilter] = useState<string>('All');
   const [statusFilter, setStatusFilter] = useState<string>('All');
   const [editingUser, setEditingUser] = useState<User | null>(null);
   const [editingBookingSettings, setEditingBookingSettings] = useState<Booking | null>(null);
   const [isReassigningAgent, setIsReassigningAgent] = useState(false);
   const [actionConfirmation, setActionConfirmation] = useState<{ user: User, status: 'ACTIVE' | 'REJECTED' } | null>(null);

   // --- PROPERTY MGMT STATE ---
   const [propertySearch, setPropertySearch] = useState('');
   const [editingPropertyId, setEditingPropertyId] = useState<string | null>(null);

   // --- REFERRAL MGMT STATE ---
   const [newCampaign, setNewCampaign] = useState({
      name: '',
      benefit: '',
      rewardAmount: '50',
      thresholdAmount: '300'
   });

   // --- NOTIFICATION STATE ---
   const [notification, setNotification] = useState<{ type: 'success' | 'error', message: string } | null>(null);

   // --- EMAIL STATE ---
   const [emailSubject, setEmailSubject] = useState('');
   const [emailBody, setEmailBody] = useState('');
   const [recipientList, setRecipientList] = useState<string[]>([]);
   const [newEmail, setNewEmail] = useState('');
   const [isParsing, setIsParsing] = useState(false);
   const [isSending, setIsSending] = useState(false);
   const [isUploadingImage, setIsUploadingImage] = useState(false);
   const [uploadedImages, setUploadedImages] = useState<string[]>([]);
   const [emailSignature, setEmailSignature] = useState('Best regards,\nIndigo Beans Team');

   // --- LANDLORD MGMT STATE ---
   const [landlordSearch, setLandlordSearch] = useState('');
   const [agentSearch, setAgentSearch] = useState(''); // NEW STATE
   const [editingLandlord, setEditingLandlord] = useState<any | null>(null);
   const [isAddingLandlord, setIsAddingLandlord] = useState(false);
   const [landlordFormData, setLandlordFormData] = useState({
      fullName: '',
      email: '',
      phoneNumber: '',
      physicalAddress: '',
      agentId: ''
   });

   // --- INTERNAL NOTES STATE ---
   const [selectedTargetForNotes, setSelectedTargetForNotes] = useState<{ id: string, type: string } | null>(null);

   // --- MONITORING FILTER STATE ---
   const [agentMonitorFilter, setAgentMonitorFilter] = useState<string>('All');

   // --- BOOKING FILTER STATE ---
   const [bookingStatusFilter, setBookingStatusFilter] = useState<string>('ALL');


   const showNotification = (type: 'success' | 'error', message: string) => {
      setNotification({ type, message });
      setTimeout(() => setNotification(null), 3000);
   };


   // --- HANDLERS ---

   // User Handlers
   const handleUserUpdate = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!editingUser) return;
      await updateUser(editingUser.id, {
         name: editingUser.name,
         role: editingUser.role,
         status: editingUser.status,
         isTrusted: editingUser.isTrusted
      });
      setEditingUser(null);
      showNotification('success', 'Changes applied');
   };

   const handleConfirmAction = async () => {
      if (!actionConfirmation) return;
      await updateUser(actionConfirmation.user.id, { status: actionConfirmation.status });
      setActionConfirmation(null);
      showNotification('success', 'Changes applied');
   };

   const filteredUsers = users.filter(u => {
      const matchesSearch = u.name.toLowerCase().includes(userSearch.toLowerCase()) ||
         u.email.toLowerCase().includes(userSearch.toLowerCase());
      const matchesRole = roleFilter === 'All' || u.role === roleFilter;
      const matchesStatus = statusFilter === 'All' || u.status === statusFilter;
      return matchesSearch && matchesRole && matchesStatus;
   });

   // Property Handlers
   const filteredProperties = properties.filter(p =>
      p.title.toLowerCase().includes(propertySearch.toLowerCase()) ||
      p.location.toLowerCase().includes(propertySearch.toLowerCase())
   );

   const handleDeleteProperty = async (id: string) => {
      if (window.confirm("Are you sure you want to delete this property? This action cannot be undone.")) {
         await deleteProperty(id);
      }
   };

   // Referral Handlers
   const handleCreateCampaign = (e: React.FormEvent) => {
      e.preventDefault();
      if (!newCampaign.name || !newCampaign.benefit) return;
      addReferralCampaign({
         name: newCampaign.name,
         benefit: newCampaign.benefit,
         rewardAmount: parseFloat(newCampaign.rewardAmount),
         thresholdAmount: parseFloat(newCampaign.thresholdAmount),
         isActive: true,
         startDate: new Date().toISOString()
      });
      setNewCampaign({ name: '', benefit: '', rewardAmount: '50', thresholdAmount: '300' });
   };

   // Email Handlers
   const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files[0]) {
         const file = e.target.files[0];
         setIsParsing(true);
         const formData = new FormData();
         formData.append('file', file);

         try {
            const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
            const response = await fetch(`${apiUrl}/api/parse-recipients`, {
               method: 'POST',
               body: formData
            });
            const data = await response.json();
            if (data.success) {
               setRecipientList(prev => [...new Set([...prev, ...data.recipients])]); // Merge and Dedupe
               showNotification('success', `Found ${data.count} recipients`);
            } else {
               showNotification('error', data.error || 'Failed to parse file');
            }
         } catch (err) {
            console.error(err);
            showNotification('error', 'Server error. Ensure backend is running.');
         } finally {
            setIsParsing(false);
            e.target.value = ''; // Reset input
         }
      }
   };

   const handleAddManualEmail = (e: React.FormEvent) => {
      e.preventDefault();
      if (!newEmail) return;

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(newEmail)) {
         return showNotification('error', 'Invalid email address');
      }

      if (recipientList.includes(newEmail)) {
         return showNotification('error', 'Email already in list');
      }

      setRecipientList(prev => [...prev, newEmail]);
      setNewEmail('');
      showNotification('success', 'Recipient added');
   };

   const handleSendEmail = async () => {
      if (recipientList.length === 0) return showNotification('error', 'No recipients selected');
      if (!emailSubject) return showNotification('error', 'Subject is required');
      if (!emailBody) return showNotification('error', 'Email body is required');

      if (!window.confirm(`Are you sure you want to send this email to ${recipientList.length} recipients?`)) return;

      setIsSending(true);
      try {
         const fullBody = `${emailBody.replace(/\n/g, '<br/>')}<br/><br/>${emailSignature.replace(/\n/g, '<br/>')}`;
         const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';

         const response = await fetch(`${apiUrl}/api/send-email`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
               recipients: recipientList,
               subject: emailSubject,
               body: fullBody
            })
         });

         const data = await response.json();
         if (data.success) {
            showNotification('success', `Sent: ${data.sent}, Failed: ${data.failed}`);
            // Optional: Clear form
            // setEmailSubject('');
            // setEmailBody('');
            // setRecipientList([]);
         } else {
            showNotification('error', 'Failed to send emails');
         }
      } catch (error) {
         console.error(error);
         showNotification('error', 'Network error calling email server.');
      } finally {
         setIsSending(false);
      }
   };

   const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files[0]) {
         const file = e.target.files[0];
         setIsUploadingImage(true);
         const formData = new FormData();
         formData.append('image', file);

         try {
            const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
            const response = await fetch(`${apiUrl}/api/upload-image`, {
               method: 'POST',
               body: formData
            });
            const data = await response.json();
            if (data.success) {
               setUploadedImages(prev => [...prev, data.url]);
               showNotification('success', 'Image uploaded');
               // Automatically insert into body
               const imgTag = `<br/><img src="http://localhost:3001${data.url}" alt="Email Image" style="max-width: 100%; height: auto;" /><br/>`;
               setEmailBody(prev => prev + imgTag);
            } else {
               showNotification('error', data.error || 'Upload failed');
            }
         } catch (err) {
            console.error(err);
            showNotification('error', 'Server error uploading image.');
         } finally {
            setIsUploadingImage(false);
            e.target.value = '';
         }
      }
   };

   const removeRecipient = (emailToRemove: string) => {
      setRecipientList(recipientList.filter(e => e !== emailToRemove));
   };

   // --- Calculated Data ---
   const referralBookings = bookings.filter(b => b.referralCode);

   const sidebarItems = [
      { id: 'overview', label: 'System Overview', icon: LayoutDashboard },
      { id: 'users', label: 'User Management', icon: Users },
      { id: 'listings', label: 'Property Mgmt', icon: Building },
      { id: 'agents', label: 'Agents', icon: Briefcase }, // NEW TAB
      { id: 'bookings', label: 'Student Bookings', icon: ClipboardList },
      { id: 'referrals', label: 'Referrals', icon: Gift },
      { id: 'landlords', label: 'Landlords', icon: Home },
      { id: 'monitoring', label: 'Monitoring', icon: MessageSquare },
      { id: 'audit', label: 'Audit Logs', icon: History },
      { id: 'email', label: 'Email / SMTP', icon: Mail },
   ];

   return (
      <DashboardLayout title="Admin Control Center" sidebarItems={sidebarItems} activeTab={activeTab} onTabChange={setActiveTab}>

         {/* --- OVERVIEW TAB --- */}
         {activeTab === 'overview' && (
            <div className="space-y-8 animate-fadeIn">
               <Card className="p-6 border-l-4 border-l-[#2f5c96] bg-gradient-to-r from-blue-50 to-white">
                  <div className="flex justify-between items-start mb-4">
                     <h3 className="font-bold text-gray-900 flex items-center">
                        <ShieldCheck className="w-5 h-5 mr-2 text-[#2f5c96]" /> Admin Access ("God Mode")
                     </h3>
                     <div className="bg-white/50 backdrop-blur rounded-lg p-2 border border-blue-100 flex items-center gap-2">
                        <span className="text-xs font-bold text-gray-600">Landlord Features:</span>
                        <label className="relative inline-flex items-center cursor-pointer">
                           <input
                              type="checkbox"
                              className="sr-only peer"
                              checked={isLandlordEnabled}
                              onChange={toggleLandlordFeature}
                           />
                           <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#2f5c96] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#2f5c96]"></div>
                           <span className="ml-2 text-xs font-medium text-gray-700">{isLandlordEnabled ? 'ON' : 'OFF'}</span>
                        </label>
                     </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                     <Button variant="white" onClick={() => navigate('/student/dashboard')} className="justify-start">
                        <GraduationCap className="w-4 h-4 mr-2" /> View as Student
                     </Button>
                     <Button variant="white" onClick={() => navigate('/agent/dashboard')} className="justify-start">
                        <Briefcase className="w-4 h-4 mr-2" /> View as Agent
                     </Button>
                     <Button variant="white" onClick={() => navigate('/landlord/dashboard')} className="justify-start" disabled={!isLandlordEnabled}>
                        <Home className="w-4 h-4 mr-2" /> View as Landlord
                     </Button>
                  </div>
               </Card>

               <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <StatCard label="Total Users" value={users.length.toString()} icon={Users} trend="Current Total" />
                  <StatCard label="Active Listings" value={properties.length.toString()} icon={Building} />
                  <StatCard label="Pending Verifications" value={users.filter(u => u.status === 'PENDING').length.toString()} icon={AlertCircle} color="red" />
               </div>
            </div>
         )}

         {/* --- USER MANAGEMENT TAB --- */}
         {activeTab === 'users' && (
            <div className="space-y-6 animate-fadeIn">
               <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                  <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
                  <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
                     <select
                        className="border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#2f5c96] bg-white"
                        value={roleFilter}
                        onChange={(e) => setRoleFilter(e.target.value)}
                     >
                        <option value="All">All Roles</option>
                        <option value="STUDENT">Student</option>
                        <option value="AGENT">Agent</option>
                        <option value="LANDLORD">Landlord</option>
                        <option value="ADMIN">Admin</option>
                     </select>
                     <select
                        className="border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#2f5c96] bg-white"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                     >
                        <option value="All">All Statuses</option>
                        <option value="ACTIVE">Active</option>
                        <option value="PENDING">Pending</option>
                        <option value="APPROVED">Approved</option>
                        <option value="REJECTED">Rejected</option>
                     </select>
                     <div className="relative flex-1 sm:w-64">
                        <Search className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
                        <input
                           type="text"
                           placeholder="Search users..."
                           className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#2f5c96] outline-none"
                           value={userSearch}
                           onChange={(e) => setUserSearch(e.target.value)}
                        />
                     </div>
                  </div>
               </div>

               <Card className="overflow-hidden">
                  <div className="overflow-x-auto">
                     <table className="w-full text-left text-sm">
                        <thead className="bg-gray-50 border-b border-gray-200">
                           <tr>
                              <th className="px-6 py-4 font-bold text-gray-500 uppercase tracking-wider text-xs">User</th>
                              <th className="px-6 py-4 font-bold text-gray-500 uppercase tracking-wider text-xs">Phone</th>
                              <th className="px-6 py-4 font-bold text-gray-500 uppercase tracking-wider text-xs">Role</th>
                              <th className="px-6 py-4 font-bold text-gray-500 uppercase tracking-wider text-xs">Status</th>
                              <th className="px-6 py-4 font-bold text-gray-500 uppercase tracking-wider text-xs">Trusted</th>
                              <th className="px-6 py-4 font-bold text-gray-500 uppercase tracking-wider text-xs text-right">Actions</th>
                           </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                           {filteredUsers.map((u) => (
                              <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                                 <td className="px-6 py-4">
                                    <div className="flex items-center gap-3">
                                       <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 font-bold text-xs">
                                          {u.name.charAt(0)}
                                       </div>
                                       <div>
                                          <p className="font-bold text-gray-900">{u.name}</p>
                                          <p className="text-xs text-gray-500">{u.email}</p>
                                       </div>
                                    </div>
                                 </td>
                                 <td className="px-6 py-4">
                                    <p className="text-sm font-medium text-gray-600">{u.phoneNumber || 'N/A'}</p>
                                 </td>
                                 <td className="px-6 py-4"><Badge color="blue">{u.role}</Badge></td>
                                 <td className="px-6 py-4">
                                    <Badge color={u.status === 'ACTIVE' || u.status === 'APPROVED' ? 'green' : (u.status === 'PENDING' ? 'yellow' : 'red')}>
                                       {u.status}
                                    </Badge>
                                 </td>
                                 <td className="px-6 py-4">
                                    {u.isTrusted && (u.role === 'AGENT' || u.role === 'LANDLORD') && (
                                       <Badge color="purple">Trusted</Badge>
                                    )}
                                 </td>
                                 <td className="px-6 py-4 text-right">
                                    {u.status === 'PENDING' ? (
                                       <div className="flex justify-end gap-2">
                                          <Button
                                             size="sm"
                                             className="bg-green-100 hover:bg-green-200 text-green-700 border-none px-3"
                                             onClick={() => setActionConfirmation({ user: u, status: 'ACTIVE' })}
                                          >
                                             <Check className="w-4 h-4 mr-1" /> Approve
                                          </Button>
                                          <Button
                                             size="sm"
                                             className="bg-red-100 hover:bg-red-200 text-red-700 border-none px-3"
                                             onClick={() => setActionConfirmation({ user: u, status: 'REJECTED' })}
                                          >
                                             <X className="w-4 h-4 mr-1" /> Reject
                                          </Button>
                                       </div>
                                    ) : (
                                       <div className="flex justify-end gap-2">
                                          <button className="text-blue-600 hover:bg-blue-50 p-1.5 rounded" onClick={() => setEditingUser(u)} title="Edit"><Edit2 className="w-4 h-4" /></button>
                                          <button className="text-red-600 hover:bg-red-50 p-1.5 rounded" onClick={async () => {
                                             if (window.confirm('Delete user? This will remove them from the database.')) {
                                                const result = await deleteUser(u.id);
                                                if (result.error) {
                                                   showNotification('error', 'Failed to delete user');
                                                } else {
                                                   showNotification('success', 'User deleted');
                                                }
                                             }
                                          }} title="Delete"><Trash2 className="w-4 h-4" /></button>
                                       </div>
                                    )}
                                 </td>
                              </tr>
                           ))}
                        </tbody>
                     </table>
                  </div>
               </Card>
            </div>
         )}

         {/* --- LISTINGS TAB --- */}
         {activeTab === 'listings' && !editingPropertyId && (
            <div className="space-y-6 animate-fadeIn">
               <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                  <h2 className="text-2xl font-bold text-gray-900">Property Management</h2>
                  <div className="relative w-full lg:w-64">
                     <Search className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
                     <input
                        type="text"
                        placeholder="Search properties..."
                        className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#2f5c96] outline-none"
                        value={propertySearch}
                        onChange={(e) => setPropertySearch(e.target.value)}
                     />
                  </div>
               </div>

               <Card className="overflow-hidden">
                  <div className="overflow-x-auto">
                     <table className="w-full text-left text-sm">
                        <thead className="bg-gray-50 border-b border-gray-200">
                           <tr>
                              <th className="px-6 py-4 font-bold text-gray-500 uppercase tracking-wider text-xs">Property</th>
                              <th className="px-6 py-4 font-bold text-gray-500 uppercase tracking-wider text-xs">Landlord</th>
                              <th className="px-6 py-4 font-bold text-gray-500 uppercase tracking-wider text-xs">Primary Agent</th>
                              <th className="px-6 py-4 font-bold text-gray-500 uppercase tracking-wider text-xs">Secondary Agent</th>
                              <th className="px-6 py-4 font-bold text-gray-500 uppercase tracking-wider text-xs">Price</th>
                              <th className="px-6 py-4 font-bold text-gray-500 uppercase tracking-wider text-xs">Status</th>
                              <th className="px-6 py-4 font-bold text-gray-500 uppercase tracking-wider text-xs text-right">Actions</th>
                           </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                           {filteredProperties.map(p => {
                              const landlord = users.find(u => u.id === p.landlordId);
                              return (
                                 <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-6 py-4">
                                       <div className="flex items-center gap-4">
                                          <img src={p.image} className="w-10 h-10 rounded object-cover bg-gray-200" alt="" />
                                          <div>
                                             <p className="font-bold text-gray-900 line-clamp-1">{p.title}</p>
                                             <p className="text-xs text-gray-500">{p.location}</p>
                                          </div>
                                       </div>
                                    </td>
                                    <td className="px-6 py-4">
                                       <select
                                          value={p.landlordId || ''}
                                          onChange={async (e) => {
                                             const newLandlordId = e.target.value;
                                             await updateProperty(p.id, { landlordId: newLandlordId });
                                             showNotification('success', 'Landlord updated');
                                          }}
                                          className="border border-gray-300 rounded px-2 py-1 text-xs bg-white outline-none focus:ring-2 focus:ring-[#2f5c96] w-full max-w-[150px]"
                                       >
                                          <option value="">No Landlord</option>
                                          {landlords.map(l => (
                                             <option key={l.id} value={l.id}>{l.fullName}</option>
                                          ))}
                                       </select>
                                    </td>
                                    <td className="px-6 py-4">
                                       <select
                                          value={p.agentId}
                                          onChange={async (e) => {
                                             const newAgentId = e.target.value;
                                             await updateProperty(p.id, { agentId: newAgentId });
                                             logAction('PROPERTY_REASSIGNMENT', p.id, 'property', { from: p.agentId, to: newAgentId, role: 'primary' });
                                             showNotification('success', 'Primary Agent updated');
                                          }}
                                          className="border border-gray-300 rounded px-2 py-1 text-xs bg-white outline-none focus:ring-2 focus:ring-[#2f5c96] w-full max-w-[150px]"
                                       >
                                          <option value="">Unassigned</option>
                                          {users.filter(u => u.role === UserRole.AGENT || u.role === UserRole.ADMIN).map(u => (
                                             <option key={u.id} value={u.id}>{u.name}{u.role === UserRole.ADMIN ? ' (Admin)' : ''}</option>
                                          ))}
                                       </select>
                                    </td>
                                    <td className="px-3 py-4 whitespace-nowrap">
                                       <select
                                          value={p.coAgentId || ''}
                                          onChange={async (e) => {
                                             const newCoAgentId = e.target.value;
                                             await updateProperty(p.id, { coAgentId: newCoAgentId });
                                             logAction('PROPERTY_REASSIGNMENT', p.id, 'property', { from: p.coAgentId, to: newCoAgentId, role: 'secondary' });
                                             showNotification('success', 'Secondary Agent updated');
                                          }}
                                          className="border border-gray-300 rounded px-2 py-1 text-xs bg-blue-50/50 outline-none focus:ring-2 focus:ring-[#2f5c96] w-full max-w-[150px]"
                                       >
                                          <option value="">None (Third Party)</option>
                                          {users.filter(u => u.role === UserRole.AGENT || u.role === UserRole.ADMIN).map(u => (
                                             <option key={u.id} value={u.id}>{u.name}{u.role === UserRole.ADMIN ? ' (Admin)' : ''}</option>
                                          ))}
                                       </select>
                                    </td>
                                    <td className="px-6 py-4 font-medium text-gray-900">R{p.price}</td>
                                    <td className="px-6 py-4">
                                       <select
                                          value={p.status}
                                          onChange={async (e) => {
                                             await updateProperty(p.id, { status: e.target.value as any });
                                             showNotification('success', 'Changes applied');
                                          }}
                                          className="border border-gray-300 rounded px-2 py-1 text-sm bg-white outline-none focus:ring-2 focus:ring-[#2f5c96]"
                                       >
                                          <option value="AVAILABLE">Available</option>
                                          <option value="RENTED">Rented</option>
                                          <option value="PENDING">Pending</option>
                                       </select>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                       <div className="flex justify-end gap-2">
                                          <a
                                             href={`#/property/${p.id}`}
                                             target="_blank"
                                             rel="noopener noreferrer"
                                             className="p-1.5 text-gray-500 hover:text-[#2f5c96] hover:bg-blue-50 rounded transition-colors"
                                             title="View in new tab"
                                          >
                                             <Eye className="w-4 h-4" />
                                          </a>
                                          <button
                                             onClick={() => setEditingPropertyId(p.id)}
                                             className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                             title="Edit Property"
                                          >
                                             <Edit2 className="w-4 h-4" />
                                          </button>
                                          <button
                                             onClick={() => handleDeleteProperty(p.id)}
                                             className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                                             title="Delete Property"
                                          >
                                             <Trash2 className="w-4 h-4" />
                                          </button>
                                       </div>
                                    </td>
                                 </tr>
                              );
                           })}
                        </tbody>
                     </table>
                  </div>
               </Card>
            </div>
         )}

         {/* --- EDIT PROPERTY SUB-VIEW --- */}
         {activeTab === 'listings' && editingPropertyId && (
            <div className="w-full animate-fadeIn">
               <div className="mb-6 flex items-center gap-2">
                  <Button variant="ghost" onClick={() => setEditingPropertyId(null)} className="p-2 -ml-2"><ChevronLeft className="w-5 h-5" /> Back</Button>
                  <h2 className="text-2xl font-bold text-gray-900">Editing Property</h2>
               </div>
               {/* Using the comprehensive PropertyForm for edits */}
               <PropertyForm
                  initialData={properties.find(p => p.id === editingPropertyId)}
                  onSuccess={() => setEditingPropertyId(null)}
               />
            </div>
         )}

         {/* --- AGENTS TAB --- */}
         {activeTab === 'agents' && (
            <div className="space-y-6 animate-fadeIn">
               <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                  <h2 className="text-2xl font-bold text-gray-900">Agent Management</h2>
                  <div className="relative w-full lg:w-64">
                     <Search className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
                     <input
                        type="text"
                        placeholder="Search agents..."
                        className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#2f5c96] outline-none"
                        value={agentSearch}
                        onChange={(e) => setAgentSearch(e.target.value)}
                     />
                  </div>
               </div>

               <Card className="overflow-hidden">
                  <div className="overflow-x-auto">
                     <table className="w-full text-left text-sm">
                        <thead className="bg-gray-50 border-b border-gray-200">
                           <tr>
                              <th className="px-6 py-4 font-bold text-gray-500 uppercase tracking-wider text-xs">Agent</th>
                              <th className="px-6 py-4 font-bold text-gray-500 uppercase tracking-wider text-xs">Institution / Agency</th>
                              <th className="px-6 py-4 font-bold text-gray-500 uppercase tracking-wider text-xs">Listings</th>
                              <th className="px-6 py-4 font-bold text-gray-500 uppercase tracking-wider text-xs">Status</th>
                              <th className="px-6 py-4 font-bold text-gray-500 uppercase tracking-wider text-xs text-right">Actions</th>
                           </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                           {users.filter(u => u.role === 'AGENT' && (u.name.toLowerCase().includes(agentSearch.toLowerCase()) || u.email.toLowerCase().includes(agentSearch.toLowerCase()))).map((agent) => {
                              const agentListings = properties.filter(p => p.agentId === agent.id).length;
                              return (
                                 <tr key={agent.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-6 py-4">
                                       <div className="flex items-center gap-3">
                                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-xs">
                                             {agent.name.charAt(0)}
                                          </div>
                                          <div>
                                             <p className="font-bold text-gray-900">{agent.name}</p>
                                             <p className="text-xs text-gray-500">{agent.email}</p>
                                          </div>
                                       </div>
                                    </td>
                                    <td className="px-6 py-4">
                                       <p className="text-sm text-gray-700">{agent.institution || agent.agencyName || 'N/A'}</p>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                       <Badge color="blue">{agentListings} Listings</Badge>
                                    </td>
                                    <td className="px-6 py-4">
                                       <Badge color={agent.status === 'ACTIVE' || agent.status === 'APPROVED' ? 'green' : 'yellow'}>{agent.status}</Badge>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                       <div className="flex justify-end gap-2">
                                          <button className="text-blue-600 hover:bg-blue-50 p-1.5 rounded" onClick={() => setEditingUser(agent)}>Edit</button>
                                          <Button size="sm" variant="ghost" className="text-gray-400 group" onClick={() => { setActiveTab('monitoring'); setAgentMonitorFilter(agent.id); }} title="Monitor Conversations">
                                             <MessageSquare className="w-4 h-4 group-hover:text-[#2f5c96]" />
                                          </Button>
                                       </div>
                                    </td>
                                 </tr>
                              );
                           })}
                        </tbody>
                     </table>
                  </div>
               </Card>
            </div>
         )}

         {/* --- BOOKINGS MANAGEMENT TAB --- */}
         {activeTab === 'bookings' && (() => {
            // Debug logging for troubleshooting
            const filteredBookings = bookings.filter(b => {
               if (bookingStatusFilter !== 'ALL' && b.status !== bookingStatusFilter) return false;
               return b.status === 'PENDING' || b.status === 'CONFIRMED' || b.status === 'COMPLETED';
            });
            const sortedBookings = [...filteredBookings].sort((a, b) => {
               const statusOrder: Record<string, number> = { 'PENDING': 0, 'CONFIRMED': 1, 'COMPLETED': 2 };
               return (statusOrder[a.status] || 99) - (statusOrder[b.status] || 99);
            });

            console.log('===== REGISTERED STUDENTS DEBUG =====');
            console.log('Total bookings:', bookings.length);
            console.log('Filtered bookings:', filteredBookings.length);
            console.log('Sorted bookings:', sortedBookings.length);
            console.log('Total users:', users.length);
            console.log('Filtered & Sorted Bookings:', sortedBookings.map(b => ({ id: b.id, status: b.status, student: b.studentId })));
            console.log('======================================');

            return (
               <div className="space-y-6 animate-fadeIn">
                  <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                     <div>
                        <h2 className="text-2xl font-bold text-gray-900">Bookings Management</h2>
                        <p className="text-gray-500">Manage all pending and confirmed bookings from the system</p>
                     </div>
                     <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Filter Status:</span>
                        <select
                           className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-[#2f5c96] bg-white min-w-[150px] shadow-sm font-medium"
                           value={bookingStatusFilter}
                           onChange={(e) => setBookingStatusFilter(e.target.value)}
                        >
                           <option value="ALL">All Active</option>
                           <option value="PENDING">Pending Only</option>
                           <option value="CONFIRMED">Confirmed Only</option>
                           <option value="COMPLETED">Completed Only</option>
                        </select>
                     </div>
                  </div>

                  <Card className="overflow-hidden">
                     <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                           <thead className="bg-gray-50 border-b border-gray-200">
                              <tr>
                                 <th className="px-6 py-4 font-bold text-gray-500 uppercase tracking-wider text-xs">Full Name</th>
                                 <th className="px-6 py-4 font-bold text-gray-500 uppercase tracking-wider text-xs">Contact Info</th>
                                 <th className="px-6 py-4 font-bold text-gray-500 uppercase tracking-wider text-xs">Property & Location</th>
                                 <th className="px-6 py-4 font-bold text-gray-500 uppercase tracking-wider text-xs">Room / Bed</th>
                                 <th className="px-6 py-4 font-bold text-gray-500 uppercase tracking-wider text-xs">Technical IDs</th>
                                 <th className="px-6 py-4 font-bold text-gray-500 uppercase tracking-wider text-xs">Status</th>
                                 <th className="px-6 py-4 font-bold text-gray-500 uppercase tracking-wider text-xs text-right">Actions</th>
                              </tr>
                           </thead>
                           <tbody className="divide-y divide-gray-100 bg-white">
                              {sortedBookings.map((booking) => {
                                 const student = users.find(u => u.id === booking.studentId);
                                 const property = properties.find(p => p.id === booking.propertyId);

                                 // Get room or bed name
                                 let roomBedName = 'Whole Unit';
                                 if (booking.roomId && property?.rooms) {
                                    const room = property.rooms.find(r => r.id === booking.roomId);
                                    if (room) {
                                       roomBedName = room.name;
                                       if (booking.bedId && room.beds) {
                                          const bed = room.beds.find(b => b.id === booking.bedId);
                                          if (bed) {
                                             roomBedName = `${room.name} - ${bed.name}`;
                                          }
                                       }
                                    }
                                 } else if (booking.bedId && property?.beds) {
                                    const bed = property.beds.find(b => b.id === booking.bedId);
                                    if (bed) {
                                       roomBedName = bed.name;
                                    }
                                 }

                                 return (
                                    <tr key={booking.id} className="hover:bg-gray-50 transition-colors">
                                       <td className="px-6 py-4">
                                          <div className="flex items-center gap-3">
                                             <div className={`w-10 h-10 rounded-full ${student ? 'bg-indigo-100 text-indigo-700' : 'bg-red-100 text-red-600'} flex items-center justify-center font-bold text-sm shadow-sm`}>
                                                {student ? student.name.charAt(0) : '?'}
                                             </div>
                                             <div>
                                                <p className="font-bold text-gray-900 leading-tight">{student ? student.name : 'Unknown Student'}</p>
                                                <p className="text-[10px] text-gray-400 font-mono mt-1">ID: {booking.studentId.substring(0, 8)}...</p>
                                             </div>
                                          </div>
                                       </td>
                                       <td className="px-6 py-4">
                                          <div className="space-y-1">
                                             <p className="text-sm text-gray-600 flex items-center gap-1.5">
                                                <Mail className="w-3 h-3 text-gray-400" /> {student ? student.email : 'N/A'}
                                             </p>
                                             <p className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                                                <Activity className="w-3 h-3 text-gray-400" /> {student ? (student.phoneNumber || 'N/A') : 'N/A'}
                                             </p>
                                          </div>
                                       </td>
                                       <td className="px-6 py-4">
                                          <p className="text-sm font-bold text-[#2f5c96] leading-tight">{property?.title || 'Unknown'}</p>
                                          <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                                             <Search className="w-3 h-3" /> {property?.location}
                                          </p>
                                       </td>
                                       <td className="px-6 py-4">
                                          <div className="space-y-2">
                                             <Badge color="purple" className="font-semibold">{roomBedName}</Badge>
                                             <p className="text-[10px] text-gray-400 font-mono">Booked on: {new Date(booking.date).toLocaleDateString()}</p>
                                          </div>
                                       </td>
                                       <td className="px-6 py-4 text-xs font-mono text-gray-500 space-y-1">
                                          <div className="flex flex-col gap-1">
                                             <div className="flex items-center gap-1">
                                                <span className="text-[9px] font-bold text-gray-400 bg-gray-100 px-1 rounded">ROOM</span>
                                                <span className="hover:text-blue-600 cursor-help" title={booking.roomId}>{booking.roomId ? booking.roomId.substring(0, 8) + '...' : 'N/A'}</span>
                                             </div>
                                             {booking.bedId && (
                                                <div className="flex items-center gap-1">
                                                   <span className="text-[9px] font-bold text-gray-400 bg-gray-100 px-1 rounded">BED</span>
                                                   <span className="hover:text-blue-600 cursor-help" title={booking.bedId}>{booking.bedId.substring(0, 8) + '...'}</span>
                                                </div>
                                             )}
                                          </div>
                                       </td>
                                       <td className="px-6 py-4">
                                          <Badge
                                             color={booking.status === 'PENDING' ? 'yellow' : (booking.status === 'CONFIRMED' ? 'blue' : (booking.status === 'COMPLETED' ? 'green' : 'red'))}
                                             className="px-3 py-1 text-[10px] font-bold tracking-wider"
                                          >
                                             {booking.status}
                                          </Badge>
                                       </td>
                                       <td className="px-6 py-4 text-right">
                                          <div className="flex justify-end gap-2">
                                             <button
                                                className="text-purple-600 hover:bg-purple-50 p-1.5 rounded transition-colors"
                                                onClick={() => setEditingBookingSettings(booking)}
                                                title="Manage Booking"
                                             >
                                                <Settings className="w-4 h-4" />
                                             </button>
                                             {student ? (
                                                <>
                                                   <button
                                                      className="text-blue-600 hover:bg-blue-50 p-1.5 rounded transition-colors"
                                                      onClick={() => setEditingUser(student)}
                                                      title="View Profile"
                                                   >
                                                      <Eye className="w-4 h-4" />
                                                   </button>
                                                   <button
                                                      className="text-green-600 hover:bg-green-50 p-1.5 rounded transition-colors"
                                                      onClick={() => {
                                                         setActiveTab('monitoring');
                                                         setAgentMonitorFilter(student.id);
                                                      }}
                                                      title="View Messages"
                                                   >
                                                      <MessageSquare className="w-4 h-4" />
                                                   </button>
                                                </>
                                             ) : (
                                                <span className="text-xs text-gray-400 italic py-1.5">No profile</span>
                                             )}
                                          </div>
                                       </td>
                                    </tr>
                                 );
                              })}
                              {filteredBookings.length === 0 && (
                                 <tr>
                                    <td colSpan={7} className="p-12 text-center text-gray-500">
                                       <div className="flex flex-col items-center gap-2">
                                          <GraduationCap className="w-12 h-12 text-gray-300 mb-2" />
                                          <p className="font-bold text-gray-900">No bookings found matching the filters.</p>
                                          <div className="mt-4 p-4 bg-gray-50 rounded-lg text-[10px] font-mono text-left inline-block border border-gray-100 max-w-md overflow-hidden">
                                             <p className="text-gray-400 mb-2 uppercase tracking-tight font-bold border-b pb-1">System Diagnostics:</p>
                                             {bookingsError && (
                                                <p className="text-red-600 font-bold mb-2">DATABASE ERROR: {bookingsError}</p>
                                             )}
                                             <p>Total Bookings In System: {bookings.length}</p>
                                             <p>Filtered (PEND/CONF/COMP): {filteredBookings.length}</p>
                                             <p>Total User Profiles: {users.length}</p>
                                             {!bookingsError && bookings.length === 0 && (
                                                <p className="text-amber-600 mt-2 italic">! Note: This might be due to missing database permissions (RLS). Please follow the instructions to apply the SQL fix if this persists.</p>
                                             )}
                                             <p className="mt-2 text-blue-500 italic font-sans">Check console logs for "REGISTERED STUDENTS DEBUG" for row-by-row mapping.</p>
                                          </div>
                                       </div>
                                    </td>
                                 </tr>
                              )}
                           </tbody>
                        </table>
                     </div>
                  </Card>
               </div>
            );
         })()}

         {/* --- BOOKING MANAGEMENT MODAL --- */}
         {editingBookingSettings && (() => {
            const booking = editingBookingSettings;
            const property = properties.find(p => p.id === booking.propertyId);
            // const student = users.find(u => u.id === booking.studentId);
            const agents = users.filter(u => u.role === UserRole.AGENT);

            return (
               <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn">
                  <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden animate-slideUp">
                     <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                        <div>
                           <h3 className="text-lg font-bold text-gray-900">Manage Student Booking</h3>
                           <p className="text-xs text-gray-500">Update status, room, or bed assignment for this student</p>
                        </div>
                        <button
                           onClick={() => setEditingBookingSettings(null)}
                           className="p-2 hover:bg-white rounded-full transition-colors border border-transparent hover:border-gray-100 shadow-sm"
                        >
                           <X className="w-5 h-5 text-gray-400" />
                        </button>
                     </div>

                     <div className="p-6 space-y-6">
                        {/* Status Section */}
                        <div className="space-y-2">
                           <label className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                              <Activity className="w-3 h-3" /> Booking Status
                           </label>
                           <select
                              className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all cursor-pointer font-medium"
                              value={booking.status}
                              onChange={(e) => updateBooking(booking.id, { status: e.target.value as any })}
                           >
                              <option value="PENDING">Pending Approval</option>
                              <option value="CONFIRMED">Confirmed / Reserved</option>
                              <option value="COMPLETED">Completed / Checked In</option>
                              <option value="CANCELLED">Cancelled / Rejected</option>
                           </select>
                        </div>

                        {/* Room Assignment */}
                        <div className="space-y-2">
                           <label className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                              <DoorOpen className="w-3 h-3" /> Room Assignment
                           </label>
                           <div className="grid gap-2">
                              {property?.rooms && property.rooms.length > 0 ? (
                                 <select
                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all cursor-pointer font-medium"
                                    value={booking.roomId || ''}
                                    onChange={(e) => updateBooking(booking.id, { roomId: e.target.value })}
                                 >
                                    <option value="">Whole Unit / Unassigned</option>
                                    {property.rooms.map(room => (
                                       <option key={room.id} value={room.id}>
                                          {room.name} (R{room.price})
                                       </option>
                                    ))}
                                 </select>
                              ) : (
                                 <div className="p-3 bg-blue-50 border border-blue-100 rounded-xl text-xs text-blue-600 flex items-center gap-2">
                                    <Info className="w-3 h-3" /> This property doesn't have individual rooms defined.
                                 </div>
                              )}
                           </div>
                        </div>

                        {/* Agent Assignment */}
                        <div className="space-y-2 pt-2 border-t border-gray-50">
                           <label className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                              <UserRound className="w-3 h-3" /> Property Agent
                           </label>
                           <div className="space-y-3">
                              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                                 <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-sm">
                                    {users.find(u => u.id === property?.agentId)?.name.charAt(0) || '?'}
                                 </div>
                                 <div className="flex-1 min-w-0">
                                    <p className="text-sm font-bold text-gray-900 truncate">
                                       {users.find(u => u.id === property?.agentId)?.name || 'Unknown Agent'}
                                    </p>
                                    <p className="text-[10px] text-gray-500 truncate">Current Property Owner</p>
                                 </div>
                                 <button
                                    onClick={() => setIsReassigningAgent(!isReassigningAgent)}
                                    className="text-xs font-bold text-blue-600 hover:text-blue-700 underline px-2 transition-colors"
                                 >
                                    {isReassigningAgent ? 'Cancel' : 'Change'}
                                 </button>
                              </div>

                              {isReassigningAgent && (
                                 <div className="space-y-2 animate-fadeIn">
                                    <select
                                       className="w-full px-4 py-3 bg-white border border-blue-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all cursor-pointer shadow-sm font-medium"
                                       value={property?.agentId || ''}
                                       onChange={async (e) => {
                                          if (property) {
                                             const success = await reassignPropertyAgent(property.id, e.target.value);
                                             if (success) setIsReassigningAgent(false);
                                          }
                                       }}
                                    >
                                       <option value="" disabled>Select New Agent...</option>
                                       {agents.map(agent => (
                                          <option key={agent.id} value={agent.id}>
                                             {agent.name} ({agent.email})
                                          </option>
                                       ))}
                                    </select>
                                    <p className="text-[10px] text-amber-600 flex items-center gap-1 px-1">
                                       <Activity className="w-2 h-2" /> Note: This changes the agent for the entire property.
                                    </p>
                                 </div>
                              )}
                           </div>
                        </div>
                     </div>

                     <div className="p-6 bg-gray-50/50 border-t border-gray-100">
                        <button
                           onClick={() => setEditingBookingSettings(null)}
                           className="w-full px-6 py-3 bg-gray-900 text-white rounded-xl text-sm font-bold hover:bg-gray-800 transition-all shadow-lg active:scale-95"
                        >
                           Done
                        </button>
                     </div>
                  </div>
               </div>
            );
         })()}

         {/* --- LANDLORDS TAB --- */}
         {activeTab === 'landlords' && (
            <div className="space-y-6 animate-fadeIn">
               <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                  <h2 className="text-2xl font-bold text-gray-900">Landlord Management</h2>
                  <div className="flex gap-2 w-full lg:w-auto">
                     <div className="relative flex-1 lg:w-64">
                        <Search className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
                        <input
                           type="text"
                           placeholder="Search landlords..."
                           className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#2f5c96] outline-none"
                           value={landlordSearch}
                           onChange={(e) => setLandlordSearch(e.target.value)}
                        />
                     </div>
                     <Button onClick={() => setIsAddingLandlord(true)}>
                        <UserPlus className="w-4 h-4 mr-2" /> Add Landlord
                     </Button>
                  </div>
               </div>

               <Card className="overflow-hidden">
                  <div className="overflow-x-auto">
                     <table className="w-full text-left text-sm">
                        <thead className="bg-gray-50 border-b border-gray-200">
                           <tr>
                              <th className="px-6 py-4 font-bold text-gray-500 uppercase tracking-wider text-xs">Landlord</th>
                              <th className="px-6 py-4 font-bold text-gray-500 uppercase tracking-wider text-xs">Contact</th>
                              <th className="px-6 py-4 font-bold text-gray-500 uppercase tracking-wider text-xs">Managed By</th>
                              <th className="px-6 py-4 font-bold text-gray-500 uppercase tracking-wider text-xs text-right">Actions</th>
                           </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                           {users.filter(u => u.role === 'LANDLORD' && (u.name.toLowerCase().includes(landlordSearch.toLowerCase()) || u.email.toLowerCase().includes(landlordSearch.toLowerCase()))).map((l) => {
                              // Link with landlord profile if it exists
                              const profile = landlords.find(lp => lp.email === l.email) || { id: l.id, fullName: l.name, email: l.email, phoneNumber: l.phoneNumber || 'N/A', physicalAddress: 'N/A', agentId: '' };
                              const managingAgent = users.find(u => u.id === profile.agentId);

                              return (
                                 <tr key={l.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-6 py-4">
                                       <div className="flex items-center gap-3">
                                          <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-bold text-xs">
                                             {l.name.charAt(0)}
                                          </div>
                                          <div>
                                             <p className="font-bold text-gray-900">{l.name}</p>
                                             <p className="text-xs text-gray-500">{l.email}</p>
                                          </div>
                                       </div>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-600">{l.phoneNumber || 'N/A'}</td>
                                    <td className="px-6 py-4">
                                       <div className="flex items-center gap-2">
                                          <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-600">
                                             {managingAgent?.name?.charAt(0) || '?'}
                                          </div>
                                          <span className="text-xs">{managingAgent?.name || 'Unassigned'}</span>
                                       </div>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                       <div className="flex justify-end gap-2">
                                          <button className="text-blue-600 hover:bg-blue-50 p-1.5 rounded" onClick={() => {
                                             setEditingLandlord(profile);
                                             setLandlordFormData({
                                                fullName: profile.fullName,
                                                email: profile.email,
                                                phoneNumber: profile.phoneNumber,
                                                physicalAddress: profile.physicalAddress || 'N/A',
                                                agentId: profile.agentId || ''
                                             });
                                          }}>Edit</button>
                                          <button className="text-red-600 hover:bg-red-50 p-1.5 rounded" onClick={async () => {
                                             if (window.confirm('Delete landlord profile?')) {
                                                await deleteLandlord(profile.id);
                                                showNotification('success', 'Landlord archived');
                                             }
                                          }}>Delete</button>
                                       </div>
                                    </td>
                                 </tr>
                              );
                           })}
                           {users.filter(u => u.role === 'LANDLORD').length === 0 && (
                              <tr><td colSpan={4} className="p-8 text-center text-gray-500">No landlords found.</td></tr>
                           )}
                        </tbody>
                     </table>
                  </div>
               </Card>
            </div>
         )}

         {/* Add Landlord Modal */}
         {(isAddingLandlord || editingLandlord) && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
               <Card className="w-full max-w-5xl p-0 flex flex-col md:flex-row overflow-hidden max-h-[90vh]">
                  <div className={`p-6 space-y-4 ${editingLandlord ? 'md:w-1/2 overflow-y-auto' : 'w-full'}`}>
                     <div className="flex justify-between items-center">
                        <h3 className="text-xl font-bold">{editingLandlord ? 'Edit Landlord' : 'Add New Landlord'}</h3>
                        {!editingLandlord && <button onClick={() => { setIsAddingLandlord(false); setLandlordFormData({ fullName: '', email: '', phoneNumber: '', physicalAddress: '', agentId: '' }); }} className="text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>}
                     </div>
                     <form onSubmit={async (e) => {
                        e.preventDefault();
                        if (editingLandlord) {
                           await updateLandlord(editingLandlord.id, landlordFormData);
                           showNotification('success', 'Landlord updated');
                        } else {
                           await addLandlord(landlordFormData);
                           showNotification('success', 'Landlord added');
                        }
                        setIsAddingLandlord(false);
                        setEditingLandlord(null);
                        setLandlordFormData({ fullName: '', email: '', phoneNumber: '', physicalAddress: '', agentId: '' });
                     }} className="space-y-3">
                        <div>
                           <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Full Name</label>
                           <input
                              className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-[#2f5c96]"
                              value={landlordFormData.fullName}
                              onChange={e => setLandlordFormData({ ...landlordFormData, fullName: e.target.value })}
                              required
                              placeholder="John Doe"
                           />
                        </div>
                        <div>
                           <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Email</label>
                           <input
                              className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-[#2f5c96]"
                              type="email"
                              value={landlordFormData.email}
                              onChange={e => setLandlordFormData({ ...landlordFormData, email: e.target.value })}
                              required
                              placeholder="john@example.com"
                           />
                        </div>
                        <div>
                           <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Phone Number</label>
                           <input
                              className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-[#2f5c96]"
                              value={landlordFormData.phoneNumber}
                              onChange={e => setLandlordFormData({ ...landlordFormData, phoneNumber: e.target.value })}
                              required
                              placeholder="+27..."
                           />
                        </div>
                        <div>
                           <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Physical Address</label>
                           <textarea
                              className="w-full border border-gray-300 rounded-lg p-2 text-sm h-20 focus:ring-2 focus:ring-[#2f5c96]"
                              value={landlordFormData.physicalAddress}
                              onChange={e => setLandlordFormData({ ...landlordFormData, physicalAddress: e.target.value })}
                              placeholder="Optional address"
                           />
                        </div>
                        <div>
                           <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Assign to Agent (Optional)</label>
                           <select
                              className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-[#2f5c96]"
                              value={landlordFormData.agentId}
                              onChange={e => setLandlordFormData({ ...landlordFormData, agentId: e.target.value })}
                           >
                              <option value="">No Agent</option>
                              {users.filter(u => u.role === UserRole.AGENT || u.role === UserRole.ADMIN).map(u => (
                                 <option key={u.id} value={u.id}>{u.name}{u.role === UserRole.ADMIN ? ' (Admin)' : ''}</option>
                              ))}
                           </select>
                        </div>
                        <div className="pt-2 flex gap-3">
                           <Button variant="ghost" type="button" onClick={() => { setIsAddingLandlord(false); setEditingLandlord(null); setLandlordFormData({ fullName: '', email: '', phoneNumber: '', physicalAddress: '', agentId: '' }); }} className="w-1/3">Cancel</Button>
                           <Button type="submit" className="flex-1">{editingLandlord ? 'Save Changes' : 'Create Landlord'}</Button>
                        </div>
                     </form>
                  </div>
                  {editingLandlord && (
                     <div className="md:w-1/2 bg-slate-50 p-6 border-l border-gray-100 overflow-y-auto relative">
                        <button onClick={() => { setIsAddingLandlord(false); setEditingLandlord(null); setLandlordFormData({ fullName: '', email: '', phoneNumber: '', physicalAddress: '', agentId: '' }); }} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
                        <InternalNotes targetId={editingLandlord.id} targetType="landlord" />
                     </div>
                  )}
               </Card>
            </div>
         )}

         {/* --- MONITORING TAB --- */}
         {activeTab === 'monitoring' && (
            <div className="space-y-6 animate-fadeIn">
               <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold text-gray-900">Communication Monitoring</h2>
                  <div className="flex items-center gap-3">
                     <select
                        className="border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#2f5c96] bg-white min-w-[200px]"
                        value={agentMonitorFilter}
                        onChange={(e) => setAgentMonitorFilter(e.target.value)}
                     >
                        <option value="All">All Agents</option>
                        {users.filter(u => u.role === 'AGENT').map(agent => (
                           <option key={agent.id} value={agent.id}>{agent.name}</option>
                        ))}
                     </select>
                     <div className="text-xs text-gray-500 italic flex items-center gap-1">
                        <ShieldCheck className="w-3.5 h-3.5" /> Read-only mode
                     </div>
                  </div>
               </div>
               <Card className="overflow-hidden">
                  <div className="overflow-x-auto">
                     <table className="w-full text-left text-sm">
                        <thead className="bg-gray-50 border-b border-gray-200">
                           <tr>
                              <th className="px-6 py-4 font-bold text-gray-500 uppercase tracking-wider text-xs">Timestamp</th>
                              <th className="px-6 py-4 font-bold text-gray-500 uppercase tracking-wider text-xs">Conversation</th>
                              <th className="px-6 py-4 font-bold text-gray-500 uppercase tracking-wider text-xs">Message Content</th>
                           </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                           {messages.filter(m => {
                              if (agentMonitorFilter === 'All') return true;
                              return m.senderId === agentMonitorFilter || m.receiverId === agentMonitorFilter;
                           }).map((m) => {
                              const sender = users.find(u => u.id === m.senderId);
                              const receiver = users.find(u => u.id === m.receiverId);
                              return (
                                 <tr key={m.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-6 py-4 text-xs text-gray-400">
                                       {new Date(m.createdAt).toLocaleString()}
                                    </td>
                                    <td className="px-6 py-4">
                                       <div className="flex flex-col gap-1">
                                          <div className="flex items-center gap-1">
                                             <span className="font-bold text-blue-600">{sender?.name || 'Unknown'}</span>
                                             <Badge color="gray" className="text-[8px] uppercase">{sender?.role}</Badge>
                                          </div>
                                          <span className="text-[10px] text-gray-400">to</span>
                                          <div className="flex items-center gap-1">
                                             <span className="font-bold text-slate-600">{receiver?.name || 'Unknown'}</span>
                                             <Badge color="gray" className="text-[8px] uppercase">{receiver?.role}</Badge>
                                          </div>
                                       </div>
                                    </td>
                                    <td className="px-6 py-4">
                                       <div className="bg-gray-50 p-3 rounded-lg border border-gray-100 italic text-gray-600 line-clamp-2 hover:line-clamp-none transition-all cursor-pointer">
                                          "{m.content}"
                                       </div>
                                    </td>
                                 </tr>
                              );
                           })}
                           {messages.length === 0 && (
                              <tr><td colSpan={3} className="p-6 text-center text-gray-500">No messages found.</td></tr>
                           )}
                        </tbody>
                     </table>
                  </div>
               </Card>
            </div>
         )}

         {/* --- AUDIT LOGS TAB --- */}
         {activeTab === 'audit' && (
            <div className="space-y-6 animate-fadeIn">
               <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold text-gray-900">System Audit Logs</h2>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                     <History className="w-4 h-4" /> Comprehensive Tracking
                  </div>
               </div>
               <Card className="overflow-hidden">
                  <div className="overflow-x-auto">
                     <table className="w-full text-left text-sm">
                        <thead className="bg-gray-50 border-b border-gray-200">
                           <tr>
                              <th className="px-6 py-4 font-bold text-gray-500 uppercase tracking-wider text-xs">Timestamp</th>
                              <th className="px-6 py-4 font-bold text-gray-500 uppercase tracking-wider text-xs">Actor</th>
                              <th className="px-6 py-4 font-bold text-gray-500 uppercase tracking-wider text-xs">Action</th>
                              <th className="px-6 py-4 font-bold text-gray-500 uppercase tracking-wider text-xs">Target Details</th>
                           </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                           {auditLogs.map((log) => {
                              const actor = users.find(u => u.id === log.userId);
                              return (
                                 <tr key={log.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-6 py-4 text-xs text-gray-400 italic">
                                       {new Date(log.createdAt).toLocaleString()}
                                    </td>
                                    <td className="px-6 py-4">
                                       <div className="flex items-center gap-2">
                                          <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-600">
                                             {actor?.name?.charAt(0) || 'S'}
                                          </div>
                                          <div>
                                             <p className="font-bold text-gray-900">{actor?.name || 'System'}</p>
                                             <p className="text-[10px] text-gray-500 uppercase">{actor?.role || 'SYSTEM'}</p>
                                          </div>
                                       </div>
                                    </td>
                                    <td className="px-6 py-4">
                                       <Badge color={
                                          log.action.includes('DELETE') ? 'red' :
                                             (log.action.includes('CREATE') ? 'green' :
                                                (log.action.includes('UPDATE') ? 'blue' : 'yellow'))
                                       }>
                                          {log.action}
                                       </Badge>
                                    </td>
                                    <td className="px-6 py-4">
                                       <div className="flex flex-col gap-0.5">
                                          <span className="text-xs font-medium text-gray-700 capitalize">{log.targetType}</span>
                                          <span className="text-[10px] text-gray-400 font-mono">{log.targetId}</span>
                                          {log.payload && (
                                             <div className="mt-1 text-[10px] text-gray-500 bg-gray-50 p-1 rounded border border-gray-100 truncate max-w-[400px]" title={JSON.stringify(log.payload)}>
                                                {JSON.stringify(log.payload)}
                                             </div>
                                          )}
                                       </div>
                                    </td>
                                 </tr>
                              );
                           })}
                           {auditLogs.length === 0 && (
                              <tr><td colSpan={4} className="p-6 text-center text-gray-500">No audit records found.</td></tr>
                           )}
                        </tbody>
                     </table>
                  </div>
               </Card>
            </div>
         )}
         {activeTab === 'referrals' && (
            <div className="space-y-12 animate-fadeIn">

               {/* Section 1: Campaign Mgmt */}
               <div className="space-y-6">
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                     <div>
                        <h2 className="text-2xl font-bold text-gray-900">Referral Campaigns</h2>
                        <p className="text-gray-500">Configure rewards and thresholds.</p>
                     </div>
                  </div>

                  {/* Create Campaign */}
                  <Card className="p-6 border-t-4 border-t-[#c19cca]">
                     <h3 className="text-lg font-bold text-gray-900 mb-4">Launch New Campaign</h3>
                     <form onSubmit={handleCreateCampaign} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                           <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Name</label>
                              <input
                                 type="text"
                                 placeholder="e.g. Back to School Bonus"
                                 className="w-full border border-gray-300 rounded-md p-2 outline-none focus:border-[#2f5c96]"
                                 value={newCampaign.name}
                                 onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
                              />
                           </div>
                           <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Benefit for Friend (Text)</label>
                              <input
                                 type="text"
                                 placeholder="e.g. Use code for discount"
                                 className="w-full border border-gray-300 rounded-md p-2 outline-none focus:border-[#2f5c96]"
                                 value={newCampaign.benefit}
                                 onChange={(e) => setNewCampaign({ ...newCampaign, benefit: e.target.value })}
                              />
                           </div>
                           <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Referrer Reward (R)</label>
                              <input
                                 type="number"
                                 className="w-full border border-gray-300 rounded-md p-2 outline-none focus:border-[#2f5c96]"
                                 value={newCampaign.rewardAmount}
                                 onChange={(e) => setNewCampaign({ ...newCampaign, rewardAmount: e.target.value })}
                              />
                           </div>
                           <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Redemption Threshold (R)</label>
                              <input
                                 type="number"
                                 className="w-full border border-gray-300 rounded-md p-2 outline-none focus:border-[#2f5c96]"
                                 value={newCampaign.thresholdAmount}
                                 onChange={(e) => setNewCampaign({ ...newCampaign, thresholdAmount: e.target.value })}
                              />
                           </div>
                        </div>
                        <div className="flex justify-end">
                           <Button type="submit" disabled={!newCampaign.name || !newCampaign.benefit}>Launch Campaign</Button>
                        </div>
                     </form>
                  </Card>

                  {/* Campaign List */}
                  <Card>
                     <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                        <h3 className="font-bold text-gray-900">Existing Campaigns</h3>
                     </div>
                     <div className="divide-y divide-gray-100">
                        {referralCampaigns.map(campaign => (
                           <div key={campaign.id} className="p-6 flex flex-col sm:flex-row justify-between items-center gap-4">
                              <div>
                                 <div className="flex items-center gap-2">
                                    <h4 className="font-bold text-gray-900">{campaign.name}</h4>
                                    <Badge color={campaign.isActive ? 'green' : 'gray'}>{campaign.isActive ? 'Active' : 'Inactive'}</Badge>
                                 </div>
                                 <p className="text-sm text-gray-600 mt-1">
                                    Reward: <span className="font-bold text-green-600">R{campaign.rewardAmount}</span> |
                                    Threshold: <span className="font-bold text-orange-600">R{campaign.thresholdAmount}</span>
                                 </p>
                                 <p className="text-xs text-gray-400 mt-1">Benefit: {campaign.benefit}</p>
                              </div>
                              <div className="flex gap-2">
                                 <Button
                                    size="sm"
                                    variant={campaign.isActive ? 'outline' : 'primary'}
                                    onClick={() => toggleReferralCampaign(campaign.id)}
                                 >
                                    {campaign.isActive ? 'Deactivate' : 'Activate'}
                                 </Button>
                              </div>
                           </div>
                        ))}
                        {referralCampaigns.length === 0 && <div className="p-6 text-center text-gray-500">No campaigns found.</div>}
                     </div>
                  </Card>
               </div>

               {/* Section 2: Withdrawal Requests */}
               <div className="space-y-4">
                  <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                     <Banknote className="w-6 h-6 text-orange-500" /> Pending Withdrawals
                  </h2>
                  <Card className="overflow-hidden">
                     <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                           <thead className="bg-orange-50 border-b border-orange-100">
                              <tr>
                                 <th className="px-6 py-3 font-bold text-orange-800 uppercase text-xs">Date</th>
                                 <th className="px-6 py-3 font-bold text-orange-800 uppercase text-xs">Student</th>
                                 <th className="px-6 py-3 font-bold text-orange-800 uppercase text-xs">Amount</th>
                                 <th className="px-6 py-3 font-bold text-orange-800 uppercase text-xs">Bank Details</th>
                                 <th className="px-6 py-3 font-bold text-orange-800 uppercase text-xs text-right">Actions</th>
                              </tr>
                           </thead>
                           <tbody className="divide-y divide-gray-100 bg-white">
                              {withdrawalRequests.filter(w => w.status === 'PENDING').map(req => {
                                 const student = users.find(u => u.id === req.userId);
                                 return (
                                    <tr key={req.id}>
                                       <td className="px-6 py-4 text-gray-500 text-xs">
                                          {new Date(req.createdAt).toLocaleDateString()}
                                       </td>
                                       <td className="px-6 py-4 font-medium text-gray-900">
                                          {student?.name || req.email}
                                       </td>
                                       <td className="px-6 py-4 font-bold text-green-600">
                                          R{req.amount}
                                       </td>
                                       <td className="px-6 py-4 text-xs text-gray-600">
                                          <div className="font-bold text-gray-800">{req.bankName}</div>
                                          <div>Acc: {req.accountNumber}</div>
                                          <div>Code: {req.branchCode}</div>
                                          <div>Name: {req.accountHolderName}</div>
                                          <div>ID: {req.idNumber}</div>
                                       </td>
                                       <td className="px-6 py-4 text-right">
                                          <div className="flex justify-end gap-2">
                                             <Button size="sm" variant="primary" onClick={() => updateWithdrawalStatus(req.id, 'PAID')}>Mark Paid</Button>
                                             <Button size="sm" variant="ghost" onClick={() => updateWithdrawalStatus(req.id, 'REJECTED')} className="text-red-500">Reject</Button>
                                          </div>
                                       </td>
                                    </tr>
                                 );
                              })}
                              {withdrawalRequests.filter(w => w.status === 'PENDING').length === 0 && (
                                 <tr><td colSpan={5} className="p-6 text-center text-gray-500">No pending requests.</td></tr>
                              )}
                           </tbody>
                        </table>
                     </div>
                  </Card>
               </div>

               {/* Section 3: Referral Activity Log */}
               <div className="space-y-4">
                  <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                     <Users className="w-6 h-6 text-blue-500" /> Referral Activity Log
                  </h2>
                  <Card className="overflow-hidden">
                     <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                           <thead className="bg-gray-50 border-b border-gray-200">
                              <tr>
                                 <th className="px-6 py-3 font-bold text-gray-500 uppercase text-xs">Date</th>
                                 <th className="px-6 py-3 font-bold text-gray-500 uppercase text-xs">Student (Applied Code)</th>
                                 <th className="px-6 py-3 font-bold text-gray-500 uppercase text-xs">Code Used</th>
                                 <th className="px-6 py-3 font-bold text-gray-500 uppercase text-xs">Referrer (Code Owner)</th>
                                 <th className="px-6 py-3 font-bold text-gray-500 uppercase text-xs">Status</th>
                              </tr>
                           </thead>
                           <tbody className="divide-y divide-gray-100 bg-white">
                              {referralBookings.map(booking => {
                                 const student = users.find(u => u.id === booking.studentId);
                                 const referrer = users.find(u => u.referralCode === booking.referralCode);

                                 return (
                                    <tr key={booking.id} className="hover:bg-gray-50">
                                       <td className="px-6 py-4 text-gray-500 text-xs">
                                          {new Date(booking.date).toLocaleDateString()}
                                       </td>
                                       <td className="px-6 py-4">
                                          <div className="font-bold text-gray-900">{student?.name || 'Unknown Student'}</div>
                                          <div className="text-xs text-gray-500">{student?.email}</div>
                                       </td>
                                       <td className="px-6 py-4">
                                          <Badge color="blue">{booking.referralCode}</Badge>
                                       </td>
                                       <td className="px-6 py-4">
                                          <div className="font-bold text-gray-900">{referrer?.name || 'Unknown Referrer'}</div>
                                          <div className="text-xs text-gray-500">{referrer?.email}</div>
                                       </td>
                                       <td className="px-6 py-4">
                                          <Badge color={booking.status === 'CONFIRMED' ? 'green' : 'yellow'}>{booking.status}</Badge>
                                       </td>
                                    </tr>
                                 );
                              })}
                              {referralBookings.length === 0 && (
                                 <tr><td colSpan={5} className="p-6 text-center text-gray-500">No referral activity yet.</td></tr>
                              )}
                           </tbody>
                        </table>
                     </div>
                  </Card>
               </div>

            </div>
         )}

         {/* --- EMAIL TAB --- */}
         {activeTab === 'email' && (
            <div className="space-y-6 animate-fadeIn">
               <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                  <div>
                     <h2 className="text-2xl font-bold text-gray-900">Email Campaign Manager</h2>
                     <p className="text-gray-500">Target your audience with bulk emails.</p>
                  </div>
               </div>

               <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                  {/* Left Column: Composition */}
                  <div className="lg:col-span-2 space-y-6">
                     <Card className="p-6">
                        <h3 className="font-bold text-gray-900 mb-4">Compose Email</h3>
                        <div className="space-y-4">
                           <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Subject Line</label>
                              <input
                                 className="w-full border border-gray-300 rounded-lg p-3 outline-none focus:ring-2 focus:ring-[#2f5c96]"
                                 placeholder="Important Update..."
                                 value={emailSubject}
                                 onChange={e => setEmailSubject(e.target.value)}
                              />
                           </div>

                           <div>
                              <div className="flex justify-between items-center mb-1">
                                 <label className="block text-sm font-medium text-gray-700">Message Body</label>
                                 <div className="flex gap-2">
                                    <label className="cursor-pointer text-[#2f5c96] hover:text-[#c19cca] text-xs font-bold flex items-center gap-1">
                                       <Image className="w-3 h-3" />
                                       {isUploadingImage ? 'Uploading...' : 'Upload Image'}
                                       <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} disabled={isUploadingImage} />
                                    </label>
                                 </div>
                              </div>
                              <div className="relative">
                                 <textarea
                                    className="w-full border border-gray-300 rounded-lg p-3 h-64 outline-none focus:ring-2 focus:ring-[#2f5c96]"
                                    placeholder="Type your message here..."
                                    value={emailBody}
                                    onChange={e => setEmailBody(e.target.value)}
                                 />
                                 <div className="absolute bottom-3 right-3 text-xs text-gray-400">
                                    HTML supported (basic)
                                 </div>
                              </div>
                           </div>

                           <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Signature</label>
                              <textarea
                                 className="w-full border border-gray-300 rounded-lg p-3 h-24 outline-none focus:ring-2 focus:ring-[#2f5c96] bg-gray-50"
                                 value={emailSignature}
                                 onChange={e => setEmailSignature(e.target.value)}
                              />
                           </div>
                        </div>

                        {uploadedImages.length > 0 && (
                           <div className="mt-4 pt-4 border-t border-gray-100">
                              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Uploaded Images</p>
                              <div className="flex flex-wrap gap-2">
                                 {uploadedImages.map((url, idx) => {
                                    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
                                    return (
                                       <div key={idx} className="relative group">
                                          <img src={`${apiUrl}${url}`} className="w-16 h-16 object-cover rounded border border-gray-200" alt="" />
                                          <button
                                             onClick={() => setEmailBody(prev => prev + `<br/><img src="${apiUrl}${url}" alt="Email Image" style="max-width: 100%; height: auto;" /><br/>`)}
                                             className="absolute inset-0 bg-black/40 text-white opacity-0 group-hover:opacity-100 flex items-center justify-center text-[10px] font-bold transition-opacity rounded"
                                          >
                                             Insert
                                          </button>
                                       </div>
                                    );
                                 })}
                              </div>
                           </div>
                        )}

                        <div className="mt-6 flex justify-end">
                           <Button
                              onClick={handleSendEmail}
                              disabled={isSending || recipientList.length === 0}
                              className={isSending ? 'opacity-75 cursor-not-allowed' : ''}
                           >
                              {isSending ? (
                                 <span className="flex items-center gap-2"><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Sending...</span>
                              ) : (
                                 <span className="flex items-center gap-2"><Mail className="w-4 h-4" /> Send to {recipientList.length} Recipients</span>
                              )}
                           </Button>
                        </div>
                     </Card>
                  </div>

                  {/* Right Column: Recipients */}
                  <div className="space-y-6">
                     <Card className="p-6 bg-gradient-to-b from-blue-50 to-white">
                        <h3 className="font-bold text-gray-900 mb-4">Recipients</h3>

                        {/* File Upload */}
                        <div className="mb-6">
                           <label className="block w-full cursor-pointer group">
                              <div className="border-2 border-dashed border-[#2f5c96]/30 group-hover:border-[#2f5c96] rounded-xl p-8 text-center transition-colors bg-white">
                                 {isParsing ? (
                                    <div className="flex flex-col items-center">
                                       <div className="w-8 h-8 border-4 border-[#2f5c96] border-t-transparent rounded-full animate-spin mb-2" />
                                       <p className="text-sm font-medium text-[#2f5c96]">Parsing file...</p>
                                    </div>
                                 ) : (
                                    <>
                                       <div className="w-12 h-12 bg-blue-100 text-[#2f5c96] rounded-full flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform">
                                          <Users className="w-6 h-6" />
                                       </div>
                                       <p className="text-sm font-bold text-gray-900">Upload Client List</p>
                                       <p className="text-xs text-gray-500 mt-1">Supports Excel (.xlsx) or Word (.docx)</p>
                                    </>
                                 )}
                              </div>
                              <input
                                 type="file"
                                 className="hidden"
                                 accept=".xlsx,.xls,.docx,.doc"
                                 onChange={handleFileUpload}
                                 disabled={isParsing}
                              />
                           </label>
                        </div>

                        {/* Stats */}
                        <div className="flex justify-between items-center mb-4 text-sm">
                           <span className="text-gray-500">Recipients Found:</span>
                           <Badge color="blue">{recipientList.length.toString()}</Badge>
                        </div>

                        {/* List */}
                        <div className="max-h-[500px] overflow-y-auto border border-gray-100 rounded-lg bg-white divide-y divide-gray-100">
                           {recipientList.length === 0 ? (
                              <div className="p-8 text-center text-gray-400 text-sm">
                                 No recipients added yet.<br />Upload a file to begin.
                              </div>
                           ) : (
                              recipientList.map((email, idx) => (
                                 <div key={idx} className="p-3 flex justify-between items-center group hover:bg-gray-50 text-sm">
                                    <span className="truncate max-w-[200px]" title={email}>{email}</span>
                                    <button
                                       onClick={() => removeRecipient(email)}
                                       className="text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                                    >
                                       <X className="w-4 h-4" />
                                    </button>
                                 </div>
                              ))
                           )}
                        </div>

                        {recipientList.length > 0 && (
                           <div className="mt-4 text-right">
                              <button onClick={() => setRecipientList([])} className="text-xs text-red-500 hover:underline">Clear All</button>
                           </div>
                        )}

                     </Card>

                     <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-xs text-amber-800">
                        <strong>Note:</strong> Emails are sent using your configured SMTP server. Ensure your lists are permission-based to avoid being flagged as spam.
                     </div>
                  </div>
               </div>
            </div>
         )}

         {/* --- CONFIRMATION MODAL --- */}
         {
            actionConfirmation && (
               <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 backdrop-blur-sm animate-fadeIn">
                  <Card className="max-w-md w-full p-6 animate-scaleIn">
                     <h3 className="font-bold text-xl mb-2 flex items-center gap-2">
                        {actionConfirmation.status === 'ACTIVE' ? <Check className="w-6 h-6 text-green-500" /> : <X className="w-6 h-6 text-red-500" />}
                        Confirm {actionConfirmation.status === 'ACTIVE' ? 'Approval' : 'Rejection'}
                     </h3>
                     <p className="text-gray-600 mb-6">
                        Are you sure you want to change <strong>{actionConfirmation.user.name}'s</strong> status to <strong>{actionConfirmation.status}</strong>?
                        {actionConfirmation.status === 'ACTIVE' && " They will be able to list properties."}
                        {actionConfirmation.status === 'REJECTED' && " They will not be able to list properties."}
                     </p>
                     <div className="flex gap-3 justify-end">
                        <Button variant="outline" onClick={() => setActionConfirmation(null)}>Cancel</Button>
                        <Button
                           className={actionConfirmation.status === 'ACTIVE' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
                           onClick={handleConfirmAction}
                        >
                           Confirm
                        </Button>
                     </div>
                  </Card>
               </div>
            )
         }

         {/* --- EDIT USER MODAL --- */}
         {
            editingUser && (
               <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 backdrop-blur-sm animate-fadeIn">
                  <Card className="max-w-4xl w-full p-0 flex flex-col md:flex-row overflow-hidden max-h-[90vh] animate-scaleIn">
                     <div className="md:w-1/2 p-6 overflow-y-auto">
                        <h3 className="font-bold text-xl mb-4">Edit User</h3>
                        <form onSubmit={handleUserUpdate} className="space-y-4">
                           <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                              <input
                                 className="w-full border p-2 rounded outline-none focus:ring-2 focus:ring-[#2f5c96]"
                                 value={editingUser.name}
                                 onChange={e => setEditingUser({ ...editingUser, name: e.target.value })}
                              />
                           </div>
                           <div className="grid grid-cols-2 gap-4">
                              <div>
                                 <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                                 <select
                                    className="w-full border p-2 rounded bg-white outline-none"
                                    value={editingUser.role}
                                    onChange={e => {
                                       const newRole = e.target.value as UserRole;
                                       const isTrusted = newRole === UserRole.ADMIN ? true : editingUser.isTrusted;
                                       setEditingUser({
                                          ...editingUser,
                                          role: newRole,
                                          isTrusted: isTrusted
                                       });
                                       // Real-time DB Update
                                       updateUser(editingUser.id, { role: newRole, isTrusted: isTrusted });
                                       showNotification('success', 'Changes applied');
                                    }}
                                 >
                                    <option value="STUDENT">Student</option>
                                    <option value="AGENT">Agent</option>
                                    <option value="LANDLORD">Landlord</option>
                                    <option value="ADMIN">Admin</option>
                                 </select>
                              </div>
                              <div>
                                 <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                 <select
                                    className="w-full border p-2 rounded bg-white outline-none"
                                    value={editingUser.status}
                                    onChange={e => {
                                       const newStatus = e.target.value as any;
                                       setEditingUser({ ...editingUser, status: newStatus });
                                       // Real-time DB Update
                                       updateUser(editingUser.id, { status: newStatus });
                                       showNotification('success', 'Changes applied');
                                    }}
                                 >
                                    <option value="ACTIVE">Active</option>
                                    <option value="PENDING">Pending</option>
                                    <option value="APPROVED">Approved</option>
                                    <option value="REJECTED">Rejected</option>
                                 </select>
                              </div>
                           </div>

                           {/* Trusted Toggle */}
                           {(editingUser.role === 'AGENT' || editingUser.role === 'LANDLORD') && (
                              <div className="flex items-center gap-2 p-3 bg-purple-50 rounded-lg border border-purple-100">
                                 <input
                                    type="checkbox"
                                    id="trustedToggle"
                                    className="w-4 h-4 text-[#2f5c96]"
                                    checked={editingUser.isTrusted || false}
                                    onChange={(e) => {
                                       const isChecked = e.target.checked;
                                       setEditingUser({ ...editingUser, isTrusted: isChecked });
                                       updateUser(editingUser.id, { isTrusted: isChecked });
                                       showNotification('success', 'Changes applied');
                                    }}
                                 />
                                 <label htmlFor="trustedToggle" className="text-sm text-purple-900 font-medium">Trusted Partner (Bypass Approval)</label>
                              </div>
                           )}

                           <div className="flex gap-2 justify-end mt-4">
                              <Button type="button" variant="outline" onClick={() => setEditingUser(null)}>Cancel</Button>
                              <Button type="submit">Save Changes</Button>
                           </div>
                        </form>
                     </div>
                     <div className="md:w-1/2 bg-slate-50 p-6 border-l border-gray-100 overflow-y-auto relative">
                        <button onClick={() => setEditingUser(null)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
                        <InternalNotes targetId={editingUser.id} targetType="user" />
                     </div>
                  </Card>
               </div>
            )
         }


         {/* Toast Notification */}
         {
            notification && (
               <div className={`fixed bottom-6 right-6 z-50 px-6 py-3 rounded-lg shadow-lg flex items-center gap-3 animate-scaleIn ${notification.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>
                  {notification.type === 'success' ? <Check className="w-5 h-5" /> : <X className="w-5 h-5" />}
                  <span className="font-medium">{notification.message}</span>
               </div>
            )
         }

      </DashboardLayout>
   );
};

